/**
 * Formula-write detection - shared traversal over `WorkbookModification`
 * payloads, used by both `excelService.ts` (to stamp
 * `appliedFormulaWrites` on the apply result and gate the per-burst
 * recalc) and `ExcelApp.tsx` (to collect FDS-bound sheet names for the
 * trailing `HarmonizedRefresh`).
 *
 * Lives in its own module so the React component (`ExcelApp.tsx`) and
 * the service layer (`excelService.ts`) can both depend on a single
 * source of truth without a circular import from the service into the
 * component.
 *
 * All functions are pure - no React, Office.js, or runtime imports.
 */

import type { WorkbookModification } from "./types";
import {
  findFqlDatePolicyIssue,
  isFdsBoundFormula,
  type FqlDatePolicyIssue,
} from "./fqlDatePolicy";

export { isFdsBoundFormula } from "./fqlDatePolicy";

export interface FqlDatePolicyViolation extends FqlDatePolicyIssue {
  formula: string;
  source:
    | "cell.formula"
    | "cell.value"
    | "range.formulas"
    | "range.values"
    | "table.rows"
    | "workbookOperation.insertTtfApiResponse";
  sheetName?: string;
  address?: string;
  rowIndex?: number;
  columnIndex?: number;
}

function violationFor(
  formula: unknown,
  location: Omit<FqlDatePolicyViolation, keyof FqlDatePolicyIssue | "formula">,
): FqlDatePolicyViolation | undefined {
  const issue = findFqlDatePolicyIssue(formula);
  if (!issue || typeof formula !== "string") return undefined;
  return { ...issue, ...location, formula };
}

/**
 * Find the first invalid FQL absolute-date representation anywhere a
 * `WorkbookModification` can carry a formula. This is a host-independent
 * preflight used before workbook mutation.
 */
export function findFqlDatePolicyViolation(
  mods: WorkbookModification,
): FqlDatePolicyViolation | undefined {
  for (const worksheet of mods.worksheets ?? []) {
    const sheetName = worksheet.sheetName;
    for (const cell of worksheet.cells ?? []) {
      const formulaIssue = violationFor(cell.formula, {
        source: "cell.formula",
        sheetName,
        address: cell.address,
      });
      if (formulaIssue) return formulaIssue;

      const valueIssue = violationFor(cell.value, {
        source: "cell.value",
        sheetName,
        address: cell.address,
      });
      if (valueIssue) return valueIssue;
    }

    for (const range of worksheet.ranges ?? []) {
      for (const [rowIndex, row] of (range.formulas ?? []).entries()) {
        for (const [columnIndex, formula] of row.entries()) {
          const issue = violationFor(formula, {
            source: "range.formulas",
            sheetName,
            address: range.address,
            rowIndex,
            columnIndex,
          });
          if (issue) return issue;
        }
      }
      for (const [rowIndex, row] of (range.values ?? []).entries()) {
        for (const [columnIndex, value] of row.entries()) {
          const issue = violationFor(value, {
            source: "range.values",
            sheetName,
            address: range.address,
            rowIndex,
            columnIndex,
          });
          if (issue) return issue;
        }
      }
    }

    for (const table of worksheet.tables ?? []) {
      for (const [rowIndex, row] of (table.rows ?? []).entries()) {
        for (const [columnIndex, value] of row.entries()) {
          const issue = violationFor(value, {
            source: "table.rows",
            sheetName,
            address: table.address,
            rowIndex,
            columnIndex,
          });
          if (issue) return issue;
        }
      }
    }
  }

  for (const operation of mods.workbookOperations ?? []) {
    if (operation.action !== "insertTtfApiResponse") continue;
    for (const [rowIndex, result] of (
      operation.insertTtfApiResponse?.apiResponse.results ?? []
    ).entries()) {
      const issue = violationFor(result.formula, {
        source: "workbookOperation.insertTtfApiResponse",
        rowIndex,
      });
      if (issue) return issue;
    }
  }

  return undefined;
}

/**
 * Predicate-driven walker over every sheet-scoped slot that the apply
 * path can treat as a formula write in a `WorkbookModification`. Backs
 * both `collectFdsBoundFormulaSheets`
 * (FDS-specific predicate, used for HarmonizedRefresh targeting) and
 * the worksheet-scoped half of `anyFormulaWrite`.
 *
 * Intentionally scoped to `worksheets[]`: the `workbookOperations[]`
 * channel can also carry formulas (notably
 * `insertTtfApiResponse.apiResponse.results[].formula`, the rare path
 * where a producer hands a pre-built TTF envelope through the
 * workbook-ops channel rather than per-cell). That channel has no
 * `sheetName` binding in the payload (the host resolves the target at
 * apply-time), so `collectFdsBoundFormulaSheets` cannot use it - but
 * `anyFormulaWrite` (which only needs a boolean) explicitly checks it
 * separately via `workbookOpHasFormula` below.
 */
export function walkFormulaCells(
  mods: WorkbookModification,
  predicate: (formula: string) => boolean,
): boolean {
  for (const ws of mods.worksheets ?? []) {
    for (const c of ws.cells ?? []) {
      if (c.formula && predicate(c.formula)) return true;
      if (typeof c.value === "string" && predicate(c.value)) return true;
    }
    for (const r of ws.ranges ?? []) {
      for (const row of r.formulas ?? []) {
        for (const v of row) {
          if (typeof v === "string" && predicate(v)) return true;
        }
      }
      for (const row of r.values ?? []) {
        for (const v of row) {
          if (typeof v === "string" && predicate(v)) return true;
        }
      }
    }
    for (const t of ws.tables ?? []) {
      for (const row of t.rows ?? []) {
        for (const v of row) {
          if (typeof v === "string" && predicate(v)) return true;
        }
      }
    }
  }
  return false;
}

/**
 * Workbook-ops channel scan — currently the only formula-writing op
 * outside `worksheets[]` is `insertTtfApiResponse`, which carries
 * `apiResponse.results[].formula: string`. If new formula-writing
 * workbook ops are added later, extend this function.
 *
 * Internal helper - exposed via `anyFormulaWrite` only.
 */
function workbookOpHasFormula(
  mods: WorkbookModification,
  predicate: (formula: string) => boolean,
): boolean {
  for (const op of mods.workbookOperations ?? []) {
    if (op.action !== "insertTtfApiResponse") continue;
    const results = op.insertTtfApiResponse?.apiResponse?.results;
    if (!results) continue;
    for (const r of results) {
      if (r.formula && predicate(r.formula)) return true;
    }
  }
  return false;
}

/**
 * True iff the burst's `WorkbookModification` payload contained at
 * least one cell-content formula (FDS or ordinary). Whitespace-tolerant
 * via `s.trimStart().startsWith("=")` to mirror the tolerance of
 * `isFdsBoundFormula`.
 *
 * Checks both `worksheets[]` (cells' `formula` and formula-like
 * `value` strings / ranges / tables) AND the workbook-ops channel
 * (`insertTtfApiResponse`) so a TTF envelope
 * handed through the workbook-ops slot still triggers the per-burst
 * recalc.
 *
 * Used to gate the per-burst `ApplicationCalculate("full")` inside the
 * apply path's manual-calc bracket — non-formula bursts (formatting,
 * sheet CRUD, plain values) skip the recalc entirely.
 */
export function anyFormulaWrite(mods: WorkbookModification): boolean {
  const predicate = (s: string) => s.trimStart().startsWith("=");
  return walkFormulaCells(mods, predicate) || workbookOpHasFormula(mods, predicate);
}

/**
 * Sheets whose `WorkbookModification` carries at least one FDS-bound
 * formula. Used by the trailing `HarmonizedRefresh` to target only the
 * sheets that need fresh FactSet data.
 *
 * Scoped to `worksheets[]` only - the workbook-ops
 * `insertTtfApiResponse` channel has no `sheetName` payload binding,
 * so a planner that emits FDS-bound formulas through that channel
 * needs to pair them with an explicit `refreshFactSetData` workbook op
 * to trigger a refresh.
 */
export function collectFdsBoundFormulaSheets(
  mods: WorkbookModification,
): string[] {
  const sheets = new Set<string>();
  for (const ws of mods.worksheets ?? []) {
    if (!ws.sheetName) continue;
    if (walkFormulaCells({ worksheets: [ws] }, isFdsBoundFormula)) {
      sheets.add(ws.sheetName);
    }
  }
  return Array.from(sheets);
}
